import { type SessionDebugSummary } from "@/shared/domain/admin/ChatDebugRecord";

/** Whole-chat token totals, ready for the admin viewer header. */
export interface SessionTokenSummary {
  /** True prompt size summed across the chat = uncached input + cache reads +
   *  cache writes. Consistent with the per-turn ``sent`` metric — bare
   *  ``input_tokens`` is only the uncached remainder and reads as ~0 on a
   *  well-cached chat. */
  sent: number;
  /** Output tokens summed across every Bedrock call. */
  received: number;
  /** Share of the sent prompt served from the prompt cache, as a whole
   *  percentage; 0 when nothing was sent. */
  cacheReadPct: number;
}

/**
 * Summarize a chat's session-wide token usage for the admin viewer header.
 *
 * Returns null when no Bedrock invocation was recorded (legacy chats / capture
 * gaps) so the caller can hide the row rather than render a misleading all-zero
 * summary. "Sent" deliberately includes cache reads and writes — see
 * {@link SessionTokenSummary.sent} and the matching note on the per-turn
 * ``summarizeTurnTokens``.
 */
export function summarizeSessionTokens(
  debugSummary: SessionDebugSummary | undefined,
): SessionTokenSummary | null {
  if (!debugSummary || debugSummary.invocations.length === 0) {
    return null;
  }
  const totals = debugSummary.totals;
  const sent =
    totals.total_input_tokens +
    totals.total_cache_read_tokens +
    totals.total_cache_write_tokens;
  return {
    sent,
    received: totals.total_output_tokens,
    cacheReadPct:
      sent > 0 ? Math.round((totals.total_cache_read_tokens / sent) * 100) : 0,
  };
}
